import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Button, Card, CardHeader, cx } from "./ui";
import { formatPrice } from "./PlanPicker";
import { ApiError } from "../lib/api";
import { relativeTime } from "../lib/format";
import { useAppRuntime, useAppScaling, useUpdateApp } from "../lib/queries";
import type { ApplicationDetail, ScaleEvent, ScaleRule } from "../lib/types";

/**
 * How many copies of an app run, by hand or by rule.
 *
 * Four rows, and the card is only as tall as the mode needs: the count and
 * what it costs, the track that sets it, one thin meter carrying the
 * reading *and* the thresholds it is read against, and -- in auto only --
 * the rule written as a sentence with its controls inline. The change bar
 * and the history appear when there is something in them.
 *
 * The first build of this was six stacked bands with a boxed rule editor
 * inside a card, and it was twice this tall for the same facts. What made
 * it shorter was noticing that most of those bands were labels: "CPU, last
 * 5 minutes" over a bar over a legend is three rows saying what one row of
 * "CPU 43% · adds above 70%" says. A label that repeats its value is the
 * thing to delete.
 *
 * The track is the control and the picture at once: filled is running,
 * outlined is asked for and not up yet, banded is the rule's range. That
 * is what the customer clicks, so the engine's own count and the wanted one
 * are visible on the same object rather than in a sentence beside it.
 *
 * The API refuses a count beside a rule; this form never sends both.
 */

const MAX = 5;
const SCALE_IN_SHARE = 0.5;
const WINDOW_MS = 5 * 60_000;

export function ScalingCard({ orgId, app, canEdit }: { orgId: string; app: ApplicationDetail; canEdit: boolean }) {
  const { t } = useTranslation("app");
  const update = useUpdateApp(orgId, app.id);
  const activity = useAppScaling(orgId, app.id);
  const runtime = useAppRuntime(orgId, app.id);

  const [auto, setAuto] = useState(app.autoscale !== null);
  const [replicas, setReplicas] = useState(app.replicas);
  const [rule, setRule] = useState<ScaleRule>(app.autoscale ?? { min: 1, max: 3, targetCpu: 70 });
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setAuto(app.autoscale !== null);
    setReplicas(app.replicas);
    if (app.autoscale) setRule(app.autoscale);
  }, [app.autoscale, app.replicas]);

  const running = runtime.data && !runtime.data.unavailable ? runtime.data.containers : null;
  const price = app.plan?.price ?? null;

  /* What the rule reads: the mean of the last five minutes' samples as a
     share of the plan's CPU allowance. Null with nothing to read, which is
     shown as such rather than as idle. */
  const load = useMemo(() => {
    const history = runtime.data?.history ?? [];
    const cpu = app.plan?.resources.cpu ?? 0;
    if (!(cpu > 0)) return null;
    const since = Date.now() - WINDOW_MS;
    const readings = history
      .filter((s) => s.cpuPercent !== null && (!s.collectedAt || Date.parse(s.collectedAt) >= since))
      .map((s) => s.cpuPercent as number);
    if (readings.length < 3) return null;
    return Math.round((readings.reduce((a, b) => a + b, 0) / readings.length / (cpu * 100)) * 100);
  }, [runtime.data, app.plan]);

  const wanted = auto ? app.replicas : replicas;
  const scaleIn = Math.round(rule.targetCpu * SCALE_IN_SHARE);
  const dirty = auto
    ? !app.autoscale || app.autoscale.min !== rule.min || app.autoscale.max !== rule.max || app.autoscale.targetCpu !== rule.targetCpu
    : app.autoscale !== null || replicas !== app.replicas;

  async function save() {
    setError(null);
    setNotice(null);
    try {
      if (auto) {
        await update.mutateAsync({ autoscale: rule });
        setNotice(t("scaling.ruleSaved", { min: rule.min, max: rule.max, target: rule.targetCpu }));
      } else if (app.autoscale) {
        // Two requests on purpose: the API refuses a count beside a rule, and
        // removing the rule first is what makes the count the customer's.
        await update.mutateAsync({ autoscale: null });
        if (replicas !== app.replicas) await update.mutateAsync({ replicas });
        setNotice(t("scaling.countSaved", { count: replicas }));
      } else {
        await update.mutateAsync({ replicas });
        setNotice(t("scaling.countSaved", { count: replicas }));
      }
      void activity.refetch();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : t("scaling.saveFailed"));
    }
  }

  const clamp = (n: number) => Math.min(MAX, Math.max(1, n));
  const setMin = (n: number) => setRule((r) => ({ ...r, min: clamp(n), max: Math.max(r.max, clamp(n)) }));
  const setMax = (n: number) => setRule((r) => ({ ...r, max: clamp(n), min: Math.min(r.min, clamp(n)) }));

  /** What Apply will do, in count and in money. */
  const summary = (() => {
    if (!dirty || !price) return dirty ? t("scaling.summaryPlain") : null;
    if (auto) {
      return `${t("scaling.summaryRule", { min: rule.min, max: rule.max, target: rule.targetCpu })} · ${formatPrice(
        price.cents * rule.min,
        price.currency,
      )}–${formatPrice(price.cents * rule.max, price.currency)}/mo`;
    }
    return `${app.replicas} → ${replicas} ${t("scaling.replicas", { count: replicas })} · ${formatPrice(
      price.cents * app.replicas,
      price.currency,
    )} → ${formatPrice(price.cents * replicas, price.currency)}/mo`;
  })();

  return (
    <Card>
      <CardHeader
        title={t("scaling.title")}
        action={
          <Segmented
            value={auto ? "auto" : "fixed"}
            disabled={!canEdit}
            options={[
              { value: "fixed", label: t("scaling.modeFixed") },
              { value: "auto", label: t("scaling.modeAuto") },
            ]}
            onChange={(v) => setAuto(v === "auto")}
          />
        }
      />

      <div className="space-y-4 p-5">
        {notice && <Alert tone="info">{notice}</Alert>}
        {error && <Alert tone="error">{error}</Alert>}

        {/* The count, its price and the engine's own answer, on one line. */}
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span className="text-2xl font-light tracking-tight tabular-nums text-fg">{wanted}</span>
          <span className="text-sm text-fg-muted">{t("scaling.replicas", { count: wanted })}</span>
          {price && (
            <span className="text-sm text-fg-subtle">
              {auto
                ? t("scaling.priceRange", {
                    low: formatPrice(price.cents * rule.min, price.currency),
                    high: formatPrice(price.cents * rule.max, price.currency),
                  })
                : t("scaling.priceEach", {
                    total: formatPrice(price.cents * wanted, price.currency),
                    each: formatPrice(price.cents, price.currency),
                  })}
            </span>
          )}
          {running !== null && running !== app.replicas && (
            <span className="ml-auto text-xs text-warn">{t("scaling.running", { running, wanted: app.replicas })}</span>
          )}
        </div>

        {/* The track: the control and the picture at once. */}
        <div className="flex gap-1" role={auto ? undefined : "radiogroup"} aria-label={t("scaling.replicasLabel")}>
          {Array.from({ length: MAX }, (_, i) => i + 1).map((n) => {
            const isRunning = running !== null ? n <= running : n <= app.replicas;
            const isWanted = n <= wanted;
            const inBand = auto && n >= rule.min && n <= rule.max;
            return (
              <button
                key={n}
                type="button"
                role={auto ? undefined : "radio"}
                aria-checked={auto ? undefined : n === replicas}
                aria-label={t("scaling.runN", { count: n })}
                disabled={!canEdit || auto}
                onClick={() => setReplicas(n)}
                className={cx(
                  "h-8 flex-1 rounded-md border text-[11px] tabular-nums transition-colors duration-200 ease-smooth focus-visible:ring-2 focus-visible:ring-accent/60 focus-visible:outline-none",
                  isWanted && isRunning
                    ? "border-accent bg-accent text-on-inverse"
                    : isWanted
                      ? "border-accent border-dashed bg-accent/15 text-accent"
                      : inBand
                        ? "border-line bg-accent-soft text-fg-subtle"
                        : "border-line text-fg-subtle",
                  auto && !inBand && "opacity-40",
                  !auto && canEdit && "hover:border-line-strong",
                )}
              >
                {n}
              </button>
            );
          })}
        </div>

        {/* The reading and the two thresholds it is read against, on one
            line: a labelled bar with a legend under it was three rows
            saying what this says in one. */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
          <span className="micro shrink-0">{t("scaling.cpu")}</span>
          <span className="relative h-1 min-w-[6rem] flex-1 rounded-full bg-raised">
            {load !== null && (
              <span
                className={cx(
                  "absolute inset-y-0 left-0 rounded-full",
                  auto && load >= rule.targetCpu ? "bg-warn" : "bg-accent",
                )}
                style={{ width: `${Math.min(100, load)}%` }}
              />
            )}
            {auto && (
              <>
                <span className="absolute inset-y-[-2px] w-px bg-fg" style={{ left: `${rule.targetCpu}%` }} />
                <span className="absolute inset-y-[-2px] w-px bg-fg-subtle" style={{ left: `${scaleIn}%` }} />
              </>
            )}
          </span>
          <span className="shrink-0 text-xs tabular-nums text-fg-muted">
            {load === null
              ? runtime.data?.monitoringConfigured === false
                ? t("scaling.loadNoMonitoring")
                : t("scaling.loadNoData")
              : `${load}%`}
          </span>
          {auto && <span className="shrink-0 text-xs text-fg-subtle">{t("scaling.thresholds", { out: rule.targetCpu, in: scaleIn })}</span>}
        </div>

        {/* The rule as a sentence with its controls in it, rather than a
            boxed form of three labelled fields inside a card. */}
        {auto && (
          <div className="flex flex-wrap items-center gap-x-2 gap-y-2 text-sm text-fg-muted">
            <span>{t("scaling.between")}</span>
            <Stepper value={rule.min} min={1} max={MAX} disabled={!canEdit} onChange={setMin} label={t("scaling.min")} />
            <span>{t("scaling.and")}</span>
            <Stepper value={rule.max} min={1} max={MAX} disabled={!canEdit} onChange={setMax} label={t("scaling.max")} />
            <span>{t("scaling.atTarget")}</span>
            <span className="flex min-w-[8rem] flex-1 items-center gap-2">
              <input
                type="range"
                min={20}
                max={95}
                step={5}
                value={rule.targetCpu}
                disabled={!canEdit}
                aria-label={t("scaling.target")}
                onChange={(e) => setRule((r) => ({ ...r, targetCpu: Number(e.target.value) }))}
                className="h-1 w-full accent-[var(--color-accent)]"
              />
              <span className="w-9 shrink-0 text-right tabular-nums text-fg">{rule.targetCpu}%</span>
            </span>
          </div>
        )}

        <p className="text-xs leading-5 text-fg-subtle">{t("scaling.stateless")}</p>
      </div>

      {/* Only when there is something to apply: an idle card carries no
          empty footer, which is most of what made the first one tall. */}
      {canEdit && dirty && (
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line bg-raised/40 px-5 py-3">
          <span className="text-sm text-fg">{summary}</span>
          <Button size="sm" onClick={() => void save()} loading={update.isPending}>
            {t("scaling.save")}
          </Button>
        </div>
      )}

      {activity.data && activity.data.data.length > 0 && (
        <div className="border-t border-line px-5 py-3">
          <ul className="space-y-1.5">
            {activity.data.data.slice(0, 4).map((event) => (
              <ActivityRow key={event.id} event={event} />
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
}

function ActivityRow({ event }: { event: ScaleEvent }) {
  const { t } = useTranslation("app");
  return (
    <li className="flex items-center gap-2 text-xs">
      <span className="w-12 shrink-0 tabular-nums text-fg">
        {event.from}&thinsp;→&thinsp;{event.to}
      </span>
      <span className={cx("shrink-0", event.reason === "manual" ? "text-fg-subtle" : "text-accent")}>
        {event.reason === "manual" ? t("scaling.byHand") : t("scaling.byRuleShort")}
      </span>
      <span className="min-w-0 flex-1 truncate text-fg-muted">
        {event.reason === "auto" && event.cpuPercent !== null
          ? t("scaling.atCpu", { cpu: event.cpuPercent })
          : event.reason === "clamp"
            ? t("scaling.byRuleRange")
            : ""}
      </span>
      <span className="shrink-0 text-fg-subtle">{relativeTime(event.createdAt)}</span>
    </li>
  );
}

/** Two options, one control: the mode, where a switch would need a sentence to say what "on" means. */
function Segmented<T extends string>({
  value,
  options,
  onChange,
  disabled,
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (value: T) => void;
  disabled?: boolean;
}) {
  return (
    <div role="radiogroup" className="inline-flex rounded-md border border-line p-0.5">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="radio"
          aria-checked={option.value === value}
          disabled={disabled}
          onClick={() => onChange(option.value)}
          className={cx(
            "rounded-[3px] px-2.5 py-1 text-xs font-medium transition-colors duration-200 ease-smooth focus-visible:ring-2 focus-visible:ring-accent/60 focus-visible:outline-none disabled:cursor-not-allowed",
            option.value === value ? "bg-inverse text-on-inverse" : "text-fg-muted hover:text-fg",
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

/** A number that only ever moves by one, sized to sit inside a sentence. */
function Stepper({
  value,
  min,
  max,
  disabled,
  onChange,
  label,
}: {
  value: number;
  min: number;
  max: number;
  disabled?: boolean;
  onChange: (value: number) => void;
  label: string;
}) {
  const { t } = useTranslation("app");
  return (
    <span className="inline-flex items-center rounded-md border border-line" role="group" aria-label={label}>
      <button
        type="button"
        aria-label={t("scaling.fewer")}
        disabled={disabled || value <= min}
        onClick={() => onChange(value - 1)}
        className="h-7 w-7 text-fg-muted hover:text-fg disabled:cursor-not-allowed disabled:text-fg-subtle"
      >
        −
      </button>
      <span className="w-5 text-center text-sm tabular-nums text-fg" aria-live="polite">
        {value}
      </span>
      <button
        type="button"
        aria-label={t("scaling.more")}
        disabled={disabled || value >= max}
        onClick={() => onChange(value + 1)}
        className="h-7 w-7 text-fg-muted hover:text-fg disabled:cursor-not-allowed disabled:text-fg-subtle"
      >
        +
      </button>
    </span>
  );
}
